// src/components/countries/CountryDiffPanel.js
// Ayarlar sayfasının "KeepGo Ülke Farkları" sekmesi. Kendi kart çerçevesi yoktur;
// SettingsPage'in container'ı içine gömülür.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './CountryDiffPanel.module.css';
import LoadingSpinner from '../common/LoadingSpinner';
import CountrySyncModal from './CountrySyncModal';
import apiClient from '../../services/ApiClient';

// BaseResponse: { data: {...} } — düz gövde ihtimalini de tolere ediyoruz.
const unwrap = (responseData) => {
    if (responseData && typeof responseData === 'object' && responseData.data) {
        return responseData.data;
    }
    return responseData;
};

const CountryDiffPanel = () => {
    const [diff, setDiff] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [activeTab, setActiveTab] = useState('missing'); // 'missing' | 'stale'
    const [onlyOwnBundle, setOnlyOwnBundle] = useState(true);
    const [syncTarget, setSyncTarget] = useState(null); // sync modalı için ülke adı

    // Salt okuma uç — güvenle her açılışta çağrılabilir, ~1–3 sn sürer.
    const fetchDiff = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/admin/bundles/country-diff');
            const data = unwrap(response.data);
            if (!data || !Array.isArray(data.missingInDb)) {
                throw new Error('Fark verisi beklenen formatta gelmedi.');
            }
            setDiff(data);
        } catch (err) {
            console.error('KeepGo ülke farkı çekilirken hata:', err);
            const apiMessage = err.response?.data?.error || err.response?.data?.message;
            setError(apiMessage || err.message || 'Fark verisi yüklenirken bir hata oluştu.');
            setDiff(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDiff();
    }, [fetchDiff]);

    // Kendi ülke paketi olanlar en üstte — satılabilir gerçek eksikler bunlar.
    const missingRows = useMemo(() => {
        const rows = diff?.missingInDb || [];
        const filtered = onlyOwnBundle ? rows.filter((r) => r.hasOwnCountryBundle) : rows;
        return [...filtered].sort((a, b) => {
            if (a.hasOwnCountryBundle !== b.hasOwnCountryBundle) {
                return a.hasOwnCountryBundle ? -1 : 1;
            }
            return (a.country || '').localeCompare(b.country || '');
        });
    }, [diff, onlyOwnBundle]);

    const staleRows = useMemo(() => {
        const rows = diff?.staleInDb || [];
        return [...rows].sort((a, b) => a.localeCompare(b));
    }, [diff]);

    const sellableCount = useMemo(
        () => (diff?.missingInDb || []).filter((r) => r.hasOwnCountryBundle).length,
        [diff]
    );

    const handleApplied = () => {
        // Fark listesi değişti — yeniden çek. Modal kapanışını kullanıcı yapar.
        fetchDiff();
    };

    return (
        <div className={styles.panel}>
            <div className={styles.toolbar}>
                <p className={styles.subtitle}>
                    KeepGo'nun sattığı ama bizim veritabanımızda olmayan ülkeleri tek tek ekleyin.
                    Ekleme hiçbir satırı silmez veya güncellemez.
                </p>
                <button type="button" className="ghost" onClick={fetchDiff} disabled={isLoading}>
                    {isLoading ? 'Yenileniyor…' : 'Yenile'}
                </button>
            </div>

            {isLoading && !diff && (
                <div className={styles.loadingContainer}>
                    <LoadingSpinner />
                    <p>KeepGo ile karşılaştırılıyor…</p>
                </div>
            )}

            {error && !diff && !isLoading && (
                <div className={styles.errorPanel}>
                    <p className={styles.errorPanelText}>{error}</p>
                    <button type="button" onClick={fetchDiff} className={`${styles.retryButton} ghost`}>
                        Tekrar dene
                    </button>
                </div>
            )}

            {error && diff && <p className={styles.errorTextPageTop}>{error}</p>}

            {diff && (
                <>
                    <div className={styles.statRow}>
                        <div className={styles.statCard}>
                            <span className={styles.statLabel}>KeepGo coverage</span>
                            <span className={`${styles.statValue} num`}>{diff.keepGoCoverageCount ?? '-'}</span>
                            <span className={styles.statHint}>KeepGo paketlerinde geçen ayrı ülke</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statLabel}>DB coverage</span>
                            <span className={`${styles.statValue} num`}>{diff.dbCoverageCount ?? '-'}</span>
                            <span className={styles.statHint}>Bizde geçen ayrı ülke adı</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statLabel}>Satılan ülke</span>
                            <span className={`${styles.statValue} num`}>{diff.dbCountryBundleCount ?? '-'}</span>
                            <span className={styles.statHint}>Uygulamanın ülke listesi</span>
                        </div>
                    </div>

                    <div className={styles.tabRow}>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === 'missing' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('missing')}
                        >
                            Eksik ülkeler
                            <span className={`${styles.tabCount} num`}>{diff.missingInDb.length}</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === 'stale' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('stale')}
                        >
                            Eski kayıtlar
                            <span className={`${styles.tabCount} num`}>{staleRows.length}</span>
                        </button>
                    </div>

                    {activeTab === 'missing' && (
                        <>
                            <div className={styles.filterRow}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={onlyOwnBundle}
                                        onChange={(e) => setOnlyOwnBundle(e.target.checked)}
                                    />
                                    Sadece kendi paketi olanlar
                                    <span className={`${styles.inlineCount} num`}>{sellableCount}</span>
                                </label>
                                <span className={styles.filterHint}>
                                    Kendi ülke paketi olmayanlar yalnızca bölgesel/global kapsamda görünür,
                                    ticari etkisi düşüktür.
                                </span>
                            </div>

                            {missingRows.length === 0 ? (
                                <div className={styles.emptyState}>
                                    {onlyOwnBundle
                                        ? 'Kendi paketi olan eksik ülke yok. Filtreyi kaldırarak tümünü görebilirsiniz.'
                                        : 'Eksik ülke yok — KeepGo coverage\'ı ile senkronsunuz.'}
                                </div>
                            ) : (
                                <div className={styles.tableWrapper}>
                                    <table className={styles.diffTable}>
                                        <thead>
                                            <tr>
                                                <th>Ülke</th>
                                                <th>Kendi paketi</th>
                                                <th>Kapsayan paketler</th>
                                                <th>İşlem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {missingRows.map((row) => (
                                                <tr key={row.country}>
                                                    <td>
                                                        <span className={styles.countryName}>{row.country}</span>
                                                        {row.absentFromAllRows && (
                                                            <span className={styles.metaNote}>
                                                                DB'de hiçbir satırda geçmiyor
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`${styles.badge} ${row.hasOwnCountryBundle ? styles.badgeOk : styles.badgeMuted}`}
                                                        >
                                                            {row.hasOwnCountryBundle ? 'Var — satılabilir' : 'Yok'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className={styles.chipRow}>
                                                            {(row.coveredByBundles || []).map((bundle) => (
                                                                <span
                                                                    key={`${row.country}-${bundle.originalDataId}`}
                                                                    className={`${styles.chip} ${styles[`chip_${bundle.bundleType}`] || ''}`}
                                                                    title={`${bundle.bundleType} · #${bundle.originalDataId}`}
                                                                >
                                                                    {bundle.bundleName}
                                                                </span>
                                                            ))}
                                                            {(row.coveredByBundles || []).length === 0 && (
                                                                <span className={styles.metaNote}>—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className={styles.actionsCell}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setSyncTarget(row.country)}
                                                        >
                                                            Ekle
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === 'stale' && (
                        <>
                            <div className={styles.infoPanel}>
                                Bu adlar veritabanımızda var ama KeepGo coverage'ında artık geçmiyor
                                (eski veya yeniden adlandırılmış paket artıkları). <strong>Salt bilgi</strong> —
                                otomatik temizlik yoktur ve olmamalı; bu satırlara bağlı satılmış siparişler var.
                            </div>
                            {staleRows.length === 0 ? (
                                <div className={styles.emptyState}>Eski kayıt yok.</div>
                            ) : (
                                <ul className={styles.staleList}>
                                    {staleRows.map((name) => (
                                        <li key={name} className={styles.staleItem}>{name}</li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </>
            )}

            {syncTarget && (
                <CountrySyncModal
                    isOpen={!!syncTarget}
                    countryName={syncTarget}
                    onClose={() => setSyncTarget(null)}
                    onApplied={handleApplied}
                />
            )}
        </div>
    );
};

export default CountryDiffPanel;
